import React, { useState } from 'react';
import {
  Trophy,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Trash2,
  Star,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateVendorSuccessStoryModal from '../../components/admin/CreateVendorSuccessStoryModal';
import { useAdminVendorSuccessStories } from '../../hooks/useAdminVendorSuccessStories';
import { useToastContext } from '../../contexts/ToastContext';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminVendorSuccessStories = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const { success, error: showError } = useToastContext();
  const {
    stories,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    statistics,
    createStory,
    isCreating,
    updateStory,
    isUpdating,
    toggleStatus,
    isToggling,
    deleteStory,
    isDeleting,
  } = useAdminVendorSuccessStories();

  const columns = [
    {
      key: 'vendor_name',
      label: 'Vendor',
      sortable: true,
      width: 'w-48',
      render: (story) => (
        <div className="flex items-center gap-3">
          {story.image && (
            <img
              src={story.image}
              alt={story.vendor_name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          )}
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{story.vendor_name || 'N/A'}</p>
            {story.featured && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <Star className="w-3 h-3 fill-current" />
                Featured
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (story) => (
        <p className="text-sm text-gray-900 dark:text-gray-100 capitalize">{story.category || 'N/A'}</p>
      ),
    },
    {
      key: 'quote',
      label: 'Quote',
      sortable: false,
      width: 'w-64',
      render: (story) => (
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">"{story.quote}"</p>
      ),
    },
    {
      key: 'author',
      label: 'Author',
      sortable: true,
      width: 'w-40',
      render: (story) => (
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{story.author}</p>
          {story.role && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{story.role}</p>
          )}
        </div>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (story) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            story.is_active
              ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
          }`}
        >
          {story.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (story) => {
        setEditingStory(story);
        setShowCreateModal(true);
      },
    },
    {
      label: (story) => (story.is_active ? 'Deactivate' : 'Activate'),
      icon: (story) => (story.is_active ? XCircle : CheckCircle),
      variant: 'default',
      onClick: (story) => {
        toggleStatus(story.id, {
          onSuccess: () => {
            success(
              'Status Updated',
              `Success story has been ${story.is_active ? 'deactivated' : 'activated'}.`
            );
          },
          onError: (err) => {
            showError('Status Update Failed', err?.response?.data?.message || 'Failed to update status.');
          },
        });
      },
      disabled: isToggling,
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (story) => {
        if (window.confirm('Are you sure you want to delete this success story?')) {
          deleteStory(story.id, {
            onSuccess: () => {
              success('Story Deleted', 'The success story has been deleted.');
            },
            onError: (err) => {
              showError('Deletion Failed', err?.response?.data?.message || 'Failed to delete.');
            },
          });
        }
      },
      disabled: isDeleting,
    },
  ];

  const handleCreate = (data) => {
    createStory(data, {
      onSuccess: () => {
        setShowCreateModal(false);
        setEditingStory(null);
        success('Story Created', 'The success story has been created.');
      },
      onError: (err) => {
        showError('Creation Failed', err?.response?.data?.message || 'Failed to create.');
      },
    });
  };

  const handleUpdate = (data) => {
    updateStory(
      { id: editingStory.id, data },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setEditingStory(null);
          success('Story Updated', 'The success story has been updated.');
        },
        onError: (err) => {
          showError('Update Failed', err?.response?.data?.message || 'Failed to update.');
        },
      }
    );
  };

  if (error) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error Loading Success Stories</h3>
          <p className="text-red-600 dark:text-red-300 text-sm">
            {error?.response?.data?.message || 'Failed to load. Please try again.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Vendor Success Stories"
        subtitle="Manage vendor success stories displayed on /vendor/success."
        actions={
          <Button
            className="!rounded-xl flex items-center gap-2"
            onClick={() => {
              setEditingStory(null);
              setShowCreateModal(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Create Story
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.vendorSuccessStories.title}
        steps={ADMIN_PAGE_GUIDES.vendorSuccessStories.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{statistics.total || 0}</p>
            </div>
            <Trophy className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{statistics.active || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Featured</p>
              <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1 tabular-nums">{statistics.featured || 0}</p>
            </div>
            <Star className="w-8 h-8 text-amber-500" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <SearchSelect
                label="Status"
                value={filters.is_active}
                onChange={(value) => handleFilterChange('is_active', value)}
                options={activeStatusOptions}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading success stories…</p>
            </div>
          ) : (
            <DataTable
              data={stories}
              columns={columns}
              actions={actions}
              isLoading={false}
              searchPlaceholder="Search by vendor, author, or quote..."
              onSearch={(_, term) => handleFilterChange('search', term)}
              emptyMessage="No success stories found"
              pageSize={10}
              showPagination={true}
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateVendorSuccessStoryModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditingStory(null);
          }}
          onSave={editingStory ? handleUpdate : handleCreate}
          story={editingStory}
          isEdit={!!editingStory}
          isLoading={isCreating || isUpdating}
        />
      )}
    </div>
  );
};

export default AdminVendorSuccessStories;
